import { css } from 'lit';

// Shop-side (operator) brand system.
//
// Goals:
//   - Professional, calm, operational. Not marketing-y.
//   - Data-legible: big tabular numbers, clear hierarchy.
//   - Brand-aligned: navy primary, gold accent (per Pag-asenso identity).
//     Coral pink is deliberately absent — it's the customer / loyalty
//     surface colour and would confuse the audience here.
//   - Semantic colour for operator status: mint = good, amber = needs
//     attention, red = bad.
//   - Same Bricolage Grotesque type family as the customer side, but
//     heavier display weight and less playful tone.
export const shopBrandStyles = css`
  :host {
    --font-display: 'Bricolage Grotesque', system-ui, -apple-system, sans-serif;
    --font-body: 'Bricolage Grotesque', system-ui, -apple-system, sans-serif;
    --font-numeric: 'Bricolage Grotesque', system-ui, sans-serif;

    /* Canvas + lines */
    --s-canvas: #ffffff;
    --s-canvas-soft: #f7f8fa;
    --s-line: #e6e9ef;
    --s-line-soft: #f0f2f6;

    /* Ink */
    --s-ink: #0c1322;
    --s-ink-soft: #4a5267;
    --s-ink-faint: #8b93a8;

    /* Brand — aliased to the canonical --brand-* ramp in global.css so
       the operator pages share one navy/gold source with the restyle.
       (Dark mode below intentionally inverts --s-navy to a light ink.) */
    --s-navy: var(--brand-navy-900);
    --s-navy-soft: var(--brand-navy-700);
    --s-gold: var(--brand-gold-500);
    --s-gold-bright: var(--brand-gold-400);
    --s-gold-glow: rgba(242, 180, 65, 0.22);
    --s-navy-glow: rgba(15, 30, 55, 0.2);

    /* Operator status */
    --s-good: #16a36b;
    --s-good-tint: #dff5e8;
    --s-warn: #d97706;
    --s-warn-tint: #fef0d7;
    --s-bad: #dc2626;
    --s-bad-tint: #fde2e2;
    --s-info: #2563eb;
    --s-info-tint: #dde7fb;

    /* Shadows */
    --s-shadow-soft: 0 1px 2px rgba(12, 19, 34, 0.04);
    --s-shadow-lift:
      0 6px 20px rgba(12, 19, 34, 0.08), 0 2px 4px rgba(12, 19, 34, 0.04);
    --s-shadow-pop: 0 14px 36px rgba(12, 19, 34, 0.12);
  }

  :host-context(.sl-theme-dark) {
    --s-canvas: #0e1320;
    --s-canvas-soft: #161c2b;
    --s-line: #242b3c;
    --s-line-soft: #1c2230;
    --s-ink: #f4f5fa;
    --s-ink-soft: #c1c6d6;
    --s-ink-faint: #7a8095;
    --s-navy: #ffffff;
    --s-navy-soft: #c1c6d6;
    --s-gold: #ffc83d;
    --s-gold-bright: #ffd76b;
    --s-good-tint: rgba(22, 163, 107, 0.16);
    --s-warn-tint: rgba(217, 119, 6, 0.18);
    --s-bad-tint: rgba(220, 38, 38, 0.18);
    --s-info-tint: rgba(37, 99, 235, 0.18);
    --s-shadow-soft: 0 1px 2px rgba(0, 0, 0, 0.4);
    --s-shadow-lift: 0 6px 20px rgba(0, 0, 0, 0.5);
    --s-shadow-pop: 0 14px 36px rgba(0, 0, 0, 0.6);
  }

  /* ============================================== */
  /* Page chrome                                    */
  /* ============================================== */
  .shop-page {
    max-width: 1200px;
    margin: 0 auto;
    padding: clamp(1rem, 3vw, 1.75rem);
    background: var(--s-canvas);
    color: var(--s-ink);
    font-family: var(--font-body);
    -webkit-font-smoothing: antialiased;
    box-sizing: border-box;
  }

  /* ============================================== */
  /* Display + text                                 */
  /* ============================================== */
  .display {
    font-family: var(--font-display);
    font-weight: 800;
    letter-spacing: -0.025em;
    color: var(--s-ink);
    line-height: 1.05;
    margin: 0;
  }
  .eyebrow {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--s-ink-faint);
  }

  /* ============================================== */
  /* Cards                                          */
  /* ============================================== */
  .shop-card {
    background: var(--s-canvas);
    border: 1px solid var(--s-line);
    border-radius: 14px;
    padding: 1.1rem 1.25rem;
    box-shadow: var(--s-shadow-soft);
  }
  .shop-card.dense {
    padding: 0.95rem 1.1rem;
  }
  .shop-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 0.85rem;
  }
  .shop-card-title {
    font-family: var(--font-display);
    font-weight: 700;
    font-size: 0.95rem;
    color: var(--s-ink);
    letter-spacing: -0.005em;
  }

  /* ============================================== */
  /* KPI tile                                       */
  /* ============================================== */
  .kpi-tile {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 1.1rem 1.25rem;
    background: var(--s-canvas);
    border: 1px solid var(--s-line);
    border-radius: 14px;
    box-shadow: var(--s-shadow-soft);
    position: relative;
    overflow: hidden;
  }
  .kpi-tile.accent-gold {
    background: linear-gradient(135deg, var(--s-navy), var(--s-navy-soft));
    color: white;
    border-color: transparent;
    box-shadow: 0 6px 18px var(--s-navy-glow);
  }
  .kpi-tile.accent-gold .kpi-label {
    color: rgba(255, 255, 255, 0.75);
  }
  .kpi-tile.accent-gold .kpi-value {
    color: var(--s-gold-bright);
  }
  .kpi-tile.accent-gold .kpi-sub {
    color: rgba(255, 255, 255, 0.75);
  }
  .kpi-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--s-ink-faint);
  }
  .kpi-value {
    font-family: var(--font-display);
    font-weight: 800;
    font-size: 1.85rem;
    color: var(--s-ink);
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.025em;
    line-height: 1.05;
  }
  .kpi-sub {
    font-size: 12px;
    font-weight: 500;
    color: var(--s-ink-soft);
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }
  .kpi-sub.up {
    color: var(--s-good);
  }
  .kpi-sub.down {
    color: var(--s-bad);
  }

  /* ============================================== */
  /* Status pill / chip                             */
  /* ============================================== */
  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.04em;
    line-height: 1.5;
  }
  .status-pill.good {
    background: var(--s-good-tint);
    color: var(--s-good);
  }
  .status-pill.warn {
    background: var(--s-warn-tint);
    color: var(--s-warn);
  }
  .status-pill.bad {
    background: var(--s-bad-tint);
    color: var(--s-bad);
  }
  .status-pill.info {
    background: var(--s-info-tint);
    color: var(--s-info);
  }
  .status-pill.neutral {
    background: var(--s-canvas-soft);
    color: var(--s-ink-soft);
  }
  .status-pill .dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: currentColor;
  }

  /* ============================================== */
  /* Buttons                                        */
  /* ============================================== */
  .shop-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 22px;
    background: var(--s-navy);
    color: white;
    border: none;
    border-radius: 999px;
    font-family: var(--font-body);
    font-weight: 700;
    font-size: 14px;
    letter-spacing: -0.005em;
    cursor: pointer;
    text-decoration: none;
    box-shadow: var(--s-shadow-soft);
    transition:
      transform 140ms ease,
      box-shadow 140ms ease,
      background 160ms ease;
    line-height: 1;
  }
  .shop-btn:hover {
    transform: translateY(-1px);
    box-shadow: var(--s-shadow-lift);
  }
  .shop-btn[disabled] {
    opacity: 0.55;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }
  .shop-btn.gold {
    background: var(--s-gold);
    color: var(--s-ink);
    box-shadow: 0 4px 12px var(--s-gold-glow);
  }
  .shop-btn.gold:hover {
    background: var(--s-gold-bright);
  }
  .shop-btn.ghost {
    background: var(--s-canvas-soft);
    color: var(--s-ink);
    border: 1px solid var(--s-line);
    box-shadow: none;
  }
  .shop-btn.ghost:hover {
    background: var(--s-canvas);
    box-shadow: var(--s-shadow-soft);
  }
  .shop-btn sl-icon {
    font-size: 16px;
  }

  /* ============================================== */
  /* Live dot (realtime/connection indicator)        */
  /* ============================================== */
  .live-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--s-good);
    box-shadow: 0 0 0 4px rgba(22, 163, 107, 0.18);
    animation: live-pulse 2s ease-in-out infinite;
  }
  .live-dot.offline {
    background: var(--s-ink-faint);
    box-shadow: 0 0 0 4px rgba(139, 147, 168, 0.16);
    animation: none;
  }
  @keyframes live-pulse {
    0%,
    100% {
      box-shadow: 0 0 0 4px rgba(22, 163, 107, 0.18);
    }
    50% {
      box-shadow: 0 0 0 7px rgba(22, 163, 107, 0.08);
    }
  }
`;
